/**
 * One finding: its claim, its edges, and the citation checks against it.
 *
 * The checks are a report and never a verdict on the reading. "Does the verse say these
 * words?" — never "is this reading sound?". A quote we cannot match against a version we
 * hold is `quote_unverified`, which is a fact about our licences rather than a defect in
 * the finding, so it is shown and never queued.
 */

import type { JSX } from 'react'

/**
 * The study workspace: the cards a reader works their own corpus in.
 *
 * Separate from `cards.tsx`, which is the *text* — passages, evidence, the
 * lexicon. That file is what the product is for; this one is where the reader
 * keeps what they found. They share the output window and its stack, because a
 * finding is a passage-shaped thing and belongs where passages live, but they
 * are different jobs and a single thousand-line file would hide that.
 *
 * Two rules from the design shape everything here:
 *
 * * **Cards are data, never closures.** Every card in the stack is a plain
 *   value — `{ kind: 'finding', id }`, never `{ onSave: () => … }` — so the
 *   stack stays serialisable and navigation is not coupled to whoever pushed
 *   it. Anything a card needs from React state arrives as a prop on the window.
 * * **Filtering, sorting and paging happen on the server.** A corpus grows
 *   without limit; a page that ships every row and hides most of them gets
 *   slower every time the reader does good work. Sorting one page in the
 *   browser would be worse still — it would reorder the window while claiming
 *   to have sorted the list.
 */

import { useState } from 'react'
import type {
  Account,
  CitationCheck,
  FindingDetail,
  FindingPage,
} from '../account'
import { acceptCheck, listFindings, readFinding, verifyFinding } from '../account'
import type { Card } from '../cards'
import { t } from '../i18n'

import { Adoption } from './adoption'
import { Failure, Loading, PAGE_SIZE, citationLine, useLoaded } from './cardDisplay'
import { originLabel } from './vocabulary'

// ---------------------------------------------------------------------------
// One finding
// ---------------------------------------------------------------------------

/**
 * Who may act on this finding, said in the header.
 *
 * A borrowed finding gets its author's name where its own edit button would be — the
 * quarantine rule made visible at the one moment a reader might try to change it.
 */
function FindingHeading({
  finding,
  onOpen,
}: {
  finding: FindingDetail
  onOpen: (card: Card) => void
}): JSX.Element {
  const borrowed = finding.imported_from !== ''

  return (
    <header className="card__head">
      <h2 className="card__kind">{t('card.finding')}</h2>
      {borrowed ? (
        <span className="badge">{t('imports.from', { author: finding.imported_from })}</span>
      ) : (
        <button
          type="button"
          className="link"
          onClick={() => onOpen({ kind: 'findingEditor', id: finding.id })}
        >
          {t('findings.edit')}
        </button>
      )}
    </header>
  )
}

/** The claim itself, and the two axes that record human judgement about it. */
function ClaimStatement({
  finding,
  account,
}: {
  finding: FindingDetail
  account: Account | null
}): JSX.Element {
  return (
    <>
      {/* The title is the claim, so it leads — the body argues it. */}
      <p className="question">{finding.title}</p>
      <p className="claim__meta">
        <span className="muted">{t(`findings.status.${finding.status}`)}</span>
        {finding.claim_type && (
          <span className="muted">{t(`claim.type.${finding.claim_type}`)}</span>
        )}
        {finding.origin && (
          <span className="muted">{originLabel(finding.origin, account)}</span>
        )}
      </p>
    </>
  )
}

/** The verses the finding rests on, each one a way back into the text. */
function ScriptureLinks({
  references,
  onOpen,
}: {
  references: string[]
  onOpen: (card: Card) => void
}): JSX.Element | null {
  if (references.length === 0) return null

  return (
    <p className="claim__meta">
      {references.map((reference) => (
        <button
          key={reference}
          type="button"
          className="link"
          onClick={() => onOpen({ kind: 'passage', reference })}
        >
          {reference}
        </button>
      ))}
    </p>
  )
}

export function FindingCard({
  id,
  account,
  onOpen,
  onStudyChanged,
}: {
  id: number
  /** Needed to say whether the stored author is the reader — see originLabel. */
  account: Account | null
  onOpen: (card: Card) => void
  /** Tells the window a check moved, so the header's queue count follows. */
  onStudyChanged?: () => void
}): JSX.Element {
  const { state, reload } = useLoaded<FindingDetail>(() => readFinding(id), `finding:${id}`)

  if (state.status === 'loading') return <Loading />
  if (state.status === 'error') return <Failure message={state.message} />

  const finding = state.data

  return (
    <article>
      <FindingHeading finding={finding} onOpen={onOpen} />

      <ClaimStatement finding={finding} account={account} />

      <div className="answer">{finding.body}</div>

      <ScriptureLinks references={finding.scripture} onOpen={onOpen} />

      {finding.adoption !== undefined && (
        <Adoption
          finding={finding}
          offer={finding.adoption}
          onOpen={onOpen}
          onAdopted={() => {
            reload()
            onStudyChanged?.()
          }}
        />
      )}

      <Edges finding={finding} onOpen={onOpen} />

      <Checks
        finding={finding}
        onAccepted={() => {
          reload()
          onStudyChanged?.()
        }}
        onVerify={async () => {
          await verifyFinding(finding.id)
          reload()
          onStudyChanged?.()
        }}
        onOpen={onOpen}
      />
    </article>
  )
}

const EDGE_KINDS = ['builds_on', 'tension_with', 'related'] as const

function Edges({
  finding,
  onOpen,
}: {
  finding: FindingDetail
  onOpen: (card: Card) => void
}) {
  const present = EDGE_KINDS.filter((kind) => finding[kind].length > 0)
  if (present.length === 0) return null

  // Every reference that no longer resolves, as a set to test against. The
  // server says which they are rather than leaving the client to find out by
  // opening each one — a link that fails only when clicked is a link that looks
  // fine until the reader has already lost their place.
  const lost = new Set(
    (finding.unresolved ?? []).map(
      (reference) => `${reference.field}:${reference.target}`,
    ),
  )

  return (
    <section className="edges">
      {present.map((kind) => (
        <p key={kind} className="claim__meta">
          <span className="muted">{t(`edge.${kind}`)}:</span>
          {finding[kind].map((slug) =>
            lost.has(`${kind}:${slug}`) ? (
              // Inert on purpose: there is nothing to open, and a disabled-looking
              // link that does nothing reads as a bug rather than as a record.
              <span key={slug} className="muted reference--lost" title={slug}>
                {t('references.deleted')}
              </span>
            ) : (
              <button
                key={slug}
                type="button"
                className="link"
                onClick={() => onOpen({ kind: 'findingBySlug', slug })}
              >
                {slug}
              </button>
            ),
          )}
        </p>
      ))}
    </section>
  )
}

/**
 * What checking this finding's citations found.
 *
 * Shown on the finding rather than only in the queue, because a reader looking
 * at a claim should be able to see whether its verses hold up without being
 * sent somewhere else — and because the ones that never enter the queue
 * (wording we have no licence to check) still belong here.
 */
function Checks({
  finding,
  onAccepted,
  onVerify,
  onOpen,
}: {
  finding: FindingDetail
  onAccepted: () => void
  onVerify: () => void | Promise<void>
  onOpen: (card: Card) => void
}) {
  const [busy, setBusy] = useState(false)
  const problems = finding.checks.filter((check) => check.is_actionable && !check.is_accepted)
  // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
  // Python constant, so the single home stays the server and this mirrors it. The
  // paydown is generating the vocabulary into TypeScript rather than carrying a marker
  // per site -- see docs/engineering-standards.md.
  const unverified = finding.checks.filter((check) => check.verdict === 'quote_unverified')

  return (
    <section className="checks">
      <h4>
        {t('citations.heading')}
        <button
          type="button"
          className="link"
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            await onVerify()
            setBusy(false)
          }}
        >
          {t('citations.recheck')}
        </button>
      </h4>

      {finding.checks.length === 0 && <p className="muted">{t('citations.none')}</p>}

      {problems.map((check) => (
        <CheckRow
          key={check.id}
          finding={finding}
          check={check}
          onAccepted={onAccepted}
          onOpen={onOpen}
        />
      ))}

      {problems.length === 0 && finding.checks.length > 0 && (
        <p className="muted">{citationLine(finding) || t('citations.allResolve', { count: finding.citations.total })}</p>
      )}

      {unverified.map((check) => (
        <p key={check.id} className="muted check check--unverified">
          <span className="badge">{t('citations.verdict.quote_unverified')}</span> {check.detail}
        </p>
      ))}
    </section>
  )
}

function CheckRow({
  finding,
  check,
  onAccepted,
  onOpen,
}: {
  finding: FindingDetail
  check: CitationCheck
  onAccepted: () => void
  onOpen: (card: Card) => void
}) {
  return (
    <div className="check check--problem">
      <p>
        <span className="badge badge--problem">{t(`citations.verdict.${check.verdict}`)}</span>{' '}
        <strong>{check.subject}</strong>
      </p>
      <p className="muted">{check.detail}</p>
      <p>
        {check.kind === 'reference' && (
          <button
            type="button"
            className="link"
            onClick={() => onOpen({ kind: 'passage', reference: check.subject })}
          >
            {t('citations.openPassage')}
          </button>
        )}
        <button
          type="button"
          className="link"
          onClick={async () => {
            await acceptCheck(finding.id, check.id)
            onAccepted()
          }}
        >
          {t('citations.accept')}
        </button>
      </p>
    </div>
  )
}

/** An edge target opened by its id, since that is all an edge carries. */
export function FindingBySlugCard({
  slug,
  corpus,
  account,
  onOpen,
  onStudyChanged,
}: {
  slug: string
  corpus: string | null
  account: Account | null
  onOpen: (card: Card) => void
  onStudyChanged?: () => void
}): JSX.Element {
  const { state } = useLoaded<FindingPage>(
    () => listFindings({ corpus: corpus ?? undefined, q: slug, limit: PAGE_SIZE }),
    `slug:${slug}:${corpus}`,
  )

  if (state.status === 'loading') return <Loading />
  if (state.status === 'error') return <Failure message={state.message} />

  const match = state.data.findings.find((finding) => finding.slug === slug)
  if (!match) return <Failure message={t('findings.missing', { slug })} />
  return (
    <FindingCard
      id={match.id}
      account={account}
      onOpen={onOpen}
      onStudyChanged={onStudyChanged}
    />
  )
}

